import { ConfirmationsPageContent } from '../app/models/aem-graphql.model';
import { environment } from '../environments/environment';

/**
 * Fallback URLs for internal vs external environments.
 * The AEM shared-nav endpoint returns environment-specific URLs at runtime,
 * but if the endpoint is unavailable we need correct fallbacks per variant.
 */
const isInternal = environment.internal;
const isProduction = environment.production;

const fallbackUrls = {
  transactionHistory: isProduction
    ? (isInternal ? 'https://transactions-i.web.vanguard.com/' : 'https://transactions.web.vanguard.com/')
    : (isInternal ? 'https://transactions-i.webt.vanguard.com/' : 'https://transactions.webt.vanguard.com/'),
  orderStatus: isProduction
    ? (isInternal ? 'https://order-status-i.web.vanguard.com/' : 'https://order-status.web.vanguard.com/')
    : (isInternal ? 'https://order-status-i.webt.vanguard.com/' : 'https://order-status.webt.vanguard.com/'),
  statements: isProduction
    ? (isInternal ? 'https://statements-i.web.vanguard.com/' : 'https://statements.web.vanguard.com/')
    : (isInternal ? 'https://statements-i.webt.vanguard.com/' : 'https://statements.webt.vanguard.com/'),
  historicalDocuments: isProduction
    ? (isInternal ? 'https://historical-documents.i.web.vanguard.com/' : 'https://historical-documents.web.vanguard.com/')
    : (isInternal ? 'https://historical-documents.i.webt.vanguard.com/' : 'https://historical-documents.webt.vanguard.com/'),
  confirmations: isProduction
    ? (isInternal ? 'https://confirmations-i.web.vanguard.com/' : 'https://confirmations.web.vanguard.com/')
    : (isInternal ? 'https://confirmations-i.webt.vanguard.com/' : 'https://confirmations.webt.vanguard.com/'),
  duplicateStatements: isProduction
    ? (isInternal ? 'https://thirdparty-documents-i.web.vanguard.com' : 'https://thirdparty-documents.web.vanguard.com')
    : (isInternal ? 'https://thirdparty-documents-i.webt.vanguard.com' : 'https://thirdparty-documents.webt.vanguard.com'),
  balanceVerificationLetter: isProduction
    ? (isInternal
        ? 'https://idpi.retailgeneral2p.c1.vanguard.com/av1-account-valuation-letter-angular/landing-page'
        : 'https://personal1.vanguard.com/usa/av1-account-valuation-letter-angular/landing-page')
    : (isInternal
        ? 'https://idpi.retailgeneral2t.c1.vanguard.com/av1-account-valuation-letter-angular/landing-page'
        : 'https://satpersonal1.vanguard.com/usa/av1-account-valuation-letter-angular/landing-page'),
  downloadCenter: environment.downloadAccountLink.url,
};

/**
 * Fallback content used when AEM GraphQL endpoints are unavailable.
 * This mirrors the structure returned by CaasService.mapToPageContent().
 */
export const fallbackContent: ConfirmationsPageContent = {
  heading: 'All accounts',
  confirmsDescription: '<p>Confirmations are available for the current year and the previous 5 years.</p>\n',
  tabOptions: {
    'tab-option-01': { optionLabel: 'Transaction history', optionUrl: fallbackUrls.transactionHistory },
    'tab-option-02': { optionLabel: 'Order status', optionUrl: fallbackUrls.orderStatus },
    'tab-option-03': { optionLabel: 'Confirmations' },
    'tab-option-04': { optionLabel: 'Statements', optionUrl: fallbackUrls.statements },
    'tab-option-05': { optionLabel: 'Historical documents', optionUrl: fallbackUrls.historicalDocuments },
  },
  dropdownOptions: {
    'option_01': { optionLabel: 'Duplicate statements and transaction confirmations', optionUrl: fallbackUrls.duplicateStatements },
    'option_02': { optionLabel: 'View or request a balance verification letter', optionUrl: fallbackUrls.balanceVerificationLetter },
  },
  viewDropdownLabel: 'View',
  viewDropdownPlaceholder: 'Select view',
  showMoreLabel: 'Show more',
  showLessLabel: 'Show less',
  sendLabel: 'Send',
  updateTableLabel: 'Update Table',
  tableSection: {
    dateColumnLabel: 'Date',
    confirmationTypeColumnLabel: 'Confirmation Type',
    descriptionColumnLabel: 'Description',
    viewDownloadColumnLabel: 'Download',
    mediaTypeColumnLabel: 'Media type',
    viewLabel: 'View',
    internalOnlyLabel: 'Internal only',
    statusColumnLabel: 'Status',
    duplicateColumnLabel: 'Duplicate',
  },
  formFields: {
    yearLabel: 'Year',
    yearAltText: 'select year',
    monthsLabel: 'Months',
    monthsPlaceholder: 'All months',
    monthsAltText: 'select month',
    confirmationTypeLabel: 'Confirmation type',
    confirmationTypePlaceholder: 'All confirmation types',
    confirmationTypeAltText: 'select confirmation type',
  },
  errorMessages: {
    noConfirmationsInPeriod: 'There are no confirmations available for this time period. Please use the dropdown to make another selection.',
    dataFetchError: 'Unable to fetch data. Try again later.',
    systemUnavailableHeading: 'Data unavailable',
    systemUnavailableBody: [
      'Our system is temporarily unable to display some of your account information. We apologize for the inconvenience and appreciate your patience while we fix these issues.',
      'Please refresh in a few minutes or choose another page.',
    ],
  },
  disclaimer: '<p>*Brokerage assets are held by Vanguard Brokerage Services, a division of Vanguard Marketing Corporation, member <a href="https://www.finra.org/investors#/" target="_blank"><b>FINRA</b></a> and <a href="https://www.sipc.org/" target="_blank"><b>SIPC</b></a>.</p>\n',
  notesAndDisclosures: '',
  downloadCenterLink: {
    label: 'Download center',
    url: fallbackUrls.downloadCenter,
  },
  seoBrowserTitle: 'Confirmations | Vanguard',
  seoMetaDescription: 'View your transaction confirmations.',
  enableNoindexRobots: true,
  enableNofollowRobots: true,
  keywordsMetaContent: 'Page, Client portfolio, Transactions, Account documents, Confirmations, activity',
  contentTypeMetaContent: 'Client portfolio',
  contentFormatMetaContent: 'Page',
  topicsMetaContent: 'vanguard:functional/retail/understanding/topics/client-intents/transactions, vanguard:functional/retail/understanding/topics/client-intents/account-documents, vanguard:functional/retail/understanding/topics/client-intents/confirmations',
  aiModel: null,
  confirmationJourneyContent: {
    internalOnlyHeading: 'Internal only',
    crewOnlyMessage: 'This page is for crew only. Clients cannot view this page.',
    sendDuplicateConfirmationLabel: 'Send duplicate confirmation',
    stepProgressTemplate: 'Step ${step-number} of 3',
    stepLabels: {
      selectRecipient: 'Select recipient',
      reviewSubmit: 'Review & submit',
      confirmation: 'Confirmation',
    },
    headers: {
      selectRecipient: 'Select recipient',
      reviewSubmit: 'Review & submit',
      confirmation: 'Confirmation',
    },
    descriptions: {
      statusReview: 'Confirm your confirmation status changes before submitting.',
      confirmationSubmitted: 'Your duplicate confirmation request has been submitted.',
    },
    tableLabels: {
      confirmationDate: 'Confirmation date',
      account: 'Account',
      recipient: 'Recipient',
      date: 'Date',
      confirmation: 'Confirmation',
      status: 'Status',
    },
    buttons: {
      back: 'Back',
      continue: 'Continue',
      submit: 'Submit',
      backToConfirmations: 'Back to confirmations',
      quit: 'Quit',
      ok: 'OK',
      changeStatus: 'Change status',
    },
    statusReviewMessages: {
      deletePermanentHeading: 'Deleting a confirmation is permanent',
      deletePermanentBody: 'You will not be able to change the status again.',
      statusUpdatedHeading: 'Confirmation status updated',
    },
  },
};
